export interface ProviderCredentialSpec {
  readonly name: string;
  readonly label: string;
  readonly secret?: boolean;
  readonly optional?: boolean;
  readonly description?: string;
}

export interface ProviderServiceSpec {
  readonly code: string;
  readonly label: string;
  readonly credentials: readonly ProviderCredentialSpec[];
}

export interface ProviderSpec {
  readonly code: string;
  readonly label: string;
  readonly services: readonly ProviderServiceSpec[];
}

const payletterCredentials = [
  { name: "CLIENT_ID", label: "클라이언트 ID" },
  { name: "PAYMENT_API_KEY", label: "결제 API 키", secret: true },
] as const;

const hectoCredentials = [
  { name: "MERCHANT_ID", label: "가맹점 ID" },
  { name: "AES_KEY", label: "AES 암호화 키", secret: true },
  { name: "LICENSE_KEY", label: "라이선스 키", secret: true },
] as const;

export const PROVIDERS: readonly ProviderSpec[] = [
  { code: "KG_FINANCIAL", label: "KG모빌리언스", services: [
    { code: "MOBILE_NON_UI_PAYMENT", label: "휴대폰 Non-UI 결제·정기결제", credentials: [
      { name: "SERVICE_ID", label: "서비스 ID", description: "KG가 발급한 숫자 12자리" },
      { name: "MERCHANT_ID", label: "가맹점 ID", description: "서비스 ID 앞 8자리와 일치하는 숫자 8자리" },
      { name: "RECORD_KEY", label: "사이트 식별값", description: "KG에 등록된 사이트 도메인, 20바이트 이내" },
      { name: "KEY_SEQUENCE", label: "암호화 키 선택", description: "기본 키는 0, 가맹점 설정 키는 1 또는 2" },
      { name: "ENCRYPTION_KEY", label: "가맹점 암호화 키", secret: true, optional: true, description: "암호화 키 선택이 1 또는 2일 때 영문·숫자 8자리 필수" },
      { name: "REQUEST_IP", label: "월결제 요청 IP", description: "KG에 등록된 서버의 IPv4 주소" },
    ] },
  ] },
  { code: "PAYLETTER", label: "페이레터", services: [
    { code: "CARD_BILLING", label: "월정액 카드 결제", credentials: payletterCredentials },
    { code: "TRANSACTION_CARD_PAYMENT", label: "거래 카드 결제", credentials: [
      ...payletterCredentials,
      { name: "SEARCH_API_KEY", label: "카드 정보 조회 API 키", secret: true, optional: true, description: "페이레터 SEARCH 키. 미등록 시 카드명 조회 없이 결제를 처리합니다." },
      { name: "CLIENT_PARTNER_ID", label: "파트너 가맹점 ID", optional: true, description: "파트너 결제 내역 조회 권한이 있는 페이레터 가맹점 ID. 카드 정보 조회 API 키와 함께 등록합니다." },
    ] },
  ] },
  { code: "DANAL", label: "다날", services: [
    { code: "IDENTITY_VERIFICATION", label: "휴대폰 문자 본인인증", credentials: [
      { name: "CP_ID", label: "다날 가맹점 ID", description: "다날에서 발급한 본인인증 가맹점 ID. 문자 소켓 인증 서비스 등록이 필요합니다." },
      { name: "CP_PASSWORD", label: "다날 가맹점 비밀번호", secret: true, description: "다날에서 발급한 가맹점 비밀번호. 서비스 화면에서는 인증번호만 입력합니다." },
    ] },
  ] },
  { code: "HECTO", label: "헥토파이낸셜", services: [
    { code: "ACCOUNT_HOLDER_VERIFICATION", label: "예금주 조회", credentials: hectoCredentials },
    { code: "TRANSFER", label: "송금", credentials: hectoCredentials },
    { code: "REFUND_TRANSFER", label: "환불 송금", credentials: [
      { name: "MERCHANT_ID", label: "가맹점 ID", description: "회원 환불 전용 Tfscp001. 거래 송금용 Tfscp000과 구분합니다." },
      ...hectoCredentials.slice(1),
    ] },
  ] },
  { code: "BIZPPURIO", label: "비즈뿌리오", services: [
    { code: "NOTIFICATION", label: "알림톡·문자", credentials: [
      { name: "ACCOUNT", label: "계정" },
      { name: "PASSWORD", label: "계정 비밀번호", secret: true },
      { name: "FROM_NUMBER", label: "발신 번호" },
      { name: "SENDER_KEY", label: "알림톡 발신 프로필 키", secret: true },
      { name: "KAPI_BIZ_ID", label: "템플릿 조회용 KAPI 계정 ID", optional: true },
      { name: "KAPI_API_KEY", label: "템플릿 조회용 KAPI 인증키", secret: true, optional: true },
      { name: "SENDER_KEY_TYPE", label: "템플릿 조회 발신키 유형 (일반 S / 그룹 G)", optional: true },
    ] },
  ] },
];

export function providerSpec(code: string): ProviderSpec | undefined {
  return PROVIDERS.find((provider) => provider.code === code);
}

export function serviceSpec(providerCode: string, serviceType: string): ProviderServiceSpec | undefined {
  return providerSpec(providerCode)?.services.find((service) => service.code === serviceType);
}

export function providerLabel(code: string): string {
  return providerSpec(code)?.label ?? code;
}

export function credentialLabel(providerCode: string, serviceType: string, name: string): string {
  return serviceSpec(providerCode, serviceType)?.credentials.find((credential) => credential.name === name)?.label ?? name;
}
